import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Chip, IconButton, ListItemIcon, MenuItem, TextField, Tooltip } from "@mui/material";
import { AddCircleOutline, DeleteOutline, EditOutlined, Refresh, StarOutline } from "@mui/icons-material";
import { MRT_ColumnDef } from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";

import ConfirmDialog from "../../components/ConfirmDialog";
import TableView from "../../components/TableView";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { createFilterOptions } from "../../util/Util";
import { canCreate, canDelete as canDeleteRow, canUpdate } from '../../util/access';
import { TEXT_FILTER_OPTIONS } from "../../util/constants";
import AddressEntryDialog from "./AddressEntryDialog";
import { SavedAddress } from "./contactBlock";

type ClientOption = { id: number; acc_name: string };

/**
 * Manage the contact blocks saved for a client. Documents keep their own copy
 * of whatever was printed, so editing here never rewrites an old invoice.
 */
const AddressBook = () => {
	const { api, access } = useAuth();
	const { t, queueReRun } = useUI();

	const [clients, setClients] = useState<ClientOption[]>([]);
	const [client, setClient] = useState<ClientOption | null>(null);
	const [rows, setRows] = useState<SavedAddress[]>([]);
	const [loading, setLoading] = useState(false);
	const [editing, setEditing] = useState<SavedAddress | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [toDelete, setToDelete] = useState<SavedAddress | null>(null);

	// The address book is a table (F-ADR-03): saving a new entry, changing a
	// saved one and removing one are its create, update and delete grants.
	const canEdit = canCreate(access, "client_address") || canUpdate(access, "client_address");
	const canDelete = canDeleteRow(access, "client_address");

	const apiRef = useRef(api);
	apiRef.current = api;
	const queueReRunRef = useRef(queueReRun);
	queueReRunRef.current = queueReRun;

	// Stable identities: these run from effects, so they must not change per render.
	const fetchClients = useCallback(async () => {
		const res = await apiRef.current.get("/clients", { params: { Active: true, Type: "CONSUMER" } });
		if (res?.status === 200) {
			const list = Array.isArray(res.data) ? res.data : [];
			list.sort((a: ClientOption, b: ClientOption) => a.acc_name.localeCompare(b.acc_name));
			setClients(list);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRunRef.current(fetchClients);
		}
	}, []);

	const fetchAddresses = useCallback(async () => {
		if (!client) {
			setRows([]);
			return;
		}
		setLoading(true);
		const res = await apiRef.current.get(`/clients/${client.id}/addresses`);
		setLoading(false);
		if (res?.status === 200) {
			setRows(Array.isArray(res.data) ? res.data : []);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRunRef.current(fetchAddresses);
		}
	}, [client]);

	useEffect(() => {
		fetchClients();
	}, [fetchClients]);

	useEffect(() => {
		fetchAddresses();
	}, [fetchAddresses]);

	const setDefault = async (entry: SavedAddress) => {
		const res = await api.post(`/clients/${client?.id}/addresses/${entry.id}/default`, {});
		if (res?.status === 204 || res?.status === 200) {
			toast.success(t("common:toast.success.addressSaved"), { autoClose: 3000 });
			fetchAddresses();
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => setDefault(entry));
		}
	};

	const remove = async () => {
		if (!toDelete) return;
		const res = await api.delete(`/clients/${client?.id}/addresses/${toDelete.id}`);
		setToDelete(null);
		if (res?.status === 204) {
			toast.success(t("common:toast.success.delete"), { autoClose: 3000 });
			fetchAddresses();
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(remove);
		}
	};

	const columns: MRT_ColumnDef<SavedAddress>[] = useMemo(() => [
		{
			header: t("common:addressLine.label"),
			accessorKey: "label",
			Cell: ({ cell, row }) => (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{String(cell.getValue() || "") || "—"}
					{row.original.is_default && (
						<Chip size="small" color="primary" label={t("common:addressBook.default")} />
					)}
				</Box>
			),
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{ header: t("common:addressLine.name"), accessorKey: "con_name", filterFn: "contains" },
		{ header: t("common:addressLine.phone"), accessorKey: "con_phone", filterFn: "contains" },
		{ header: t("common:addressLine.email"), accessorKey: "con_email", filterFn: "contains" },
		{
			header: t("common:addressLine.address"),
			accessorKey: "con_address",
			Cell: ({ cell }) => String(cell.getValue() || "") || "—",
			filterFn: "contains",
		},
	], [t]);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TableView
				resource="client_address"
				header={t("common:addressBook.title")}
				columns={columns}
				data={rows}
				isLoading={loading}
				useDateRangeFilter={false}
				enableRowActions
				filters={(
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
						<Autocomplete
							size="small"
							sx={{ minWidth: 260 }}
							options={clients}
							value={client}
							filterOptions={createFilterOptions((option: ClientOption) => option.acc_name || "")}
							getOptionLabel={(option) => option.acc_name || ""}
							isOptionEqualToValue={(option, selected) => option.id === selected?.id}
							onChange={(_, value) => setClient(value)}
							renderInput={(params) => (
								<TextField {...params} variant="filled" label={t("common:tables.client")} />
							)}
						/>
						<Tooltip title={t("common:ui.tooltips.refreshData")}>
							<span>
								<IconButton onClick={fetchAddresses} disabled={!client || loading} color="primary">
									<Refresh />
								</IconButton>
							</span>
						</Tooltip>
						{canEdit && (
							<Tooltip title={t("common:addressBook.addEntry")}>
								<span>
									<IconButton
										color="success"
										disabled={!client}
										onClick={() => {
											setEditing(null);
											setDialogOpen(true);
										}}
									>
										<AddCircleOutline />
									</IconButton>
								</span>
							</Tooltip>
						)}
					</Box>
				)}
				renderRowActionMenuItems={(row, closeMenu) => [
					canEdit && (
						<MenuItem
							key="edit"
							onClick={() => {
								setEditing(row.original);
								setDialogOpen(true);
								closeMenu();
							}}
						>
							<ListItemIcon><EditOutlined /></ListItemIcon>
							{t("common:ui.buttons.edit")}
						</MenuItem>
					),
					canEdit && !row.original.is_default && (
						<MenuItem
							key="default"
							onClick={() => {
								setDefault(row.original);
								closeMenu();
							}}
						>
							<ListItemIcon><StarOutline /></ListItemIcon>
							{t("common:addressBook.setDefault")}
						</MenuItem>
					),
					canDelete && (
						<MenuItem
							key="delete"
							sx={{ color: "error.main" }}
							onClick={() => {
								setToDelete(row.original);
								closeMenu();
							}}
						>
							<ListItemIcon sx={{ color: "error.main" }}><DeleteOutline /></ListItemIcon>
							{t("common:ui.buttons.delete")}
						</MenuItem>
					),
				]}
			/>

			{client && (
				<AddressEntryDialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					clientId={client.id}
					entry={editing}
					onSaved={fetchAddresses}
				/>
			)}

			<ConfirmDialog
				isOpen={Boolean(toDelete)}
				setIsOpen={(open: boolean) => { if (!open) setToDelete(null); }}
				title={t("common:dialog.deleteConfirmation")}
				message={t("common:addressBook.confirmDelete", {
					entry: toDelete?.label || toDelete?.con_name || "",
				})}
				onConfirm={remove}
			/>
		</LocalizationProvider>
	);
};

export default AddressBook;
